// client/src/components/pages/LexiStar/Reports/ScheduleEditor.tsx
//
// Create / edit a report schedule (ST-00188 M4-R2; FR-RPT-004; m4-contracts
// §3 report_schedules, §6): which report with which parameters (a relative
// period for period reports — the provider resolves it at run time), when
// (daily / weekly / monthly / shift-end presets, or a custom cron, in a time
// zone), in which formats, over which channels (email, Telegram, in-app), to
// whom (users, external contacts, Telegram groups, recipient groups) — each
// recipient optionally narrowed by a scope that is merged into the report's
// parameters.scope for them. Needs lexistar.reports.manage.

import { useEffect, useMemo, useState } from 'react';
import { FaPlus, FaTimes } from 'react-icons/fa';
import ParameterForm, { inputClass } from './ParameterForm';
import { ThemedSelect } from '../../../../types/commonComponents';
import ThemedTimePicker from '../../../common/ThemedTimePicker';
import TimeZoneSelect from './TimeZoneSelect';
import { DEFAULT_TIME_ZONE, canonicalTimeZone } from '../../../../utils/timeZones';
import { usePickerSheetMode } from '../../../common/pickerHooks';
import { createSchedule, errorText, updateSchedule, type UserOption } from '../../../../services/lexistarReports';
import {
  CHANNELS,
  DEFAULT_CADENCE,
  FORMAT_LABELS,
  RECIPIENT_KINDS,
  SCOPE_KEYS,
  WEEKDAYS,
  cadenceToCron,
  cronToCadence,
  defaultParams,
  describeCron,
  scheduleErrors,
  schemaFields,
  scopeValues,
  type Cadence,
  type CadenceKind,
} from './reportLogic';
import type {
  Channel,
  RecipientKind,
  ReportDefinition,
  ReportFormat,
  ReportSchedule,
  ReportScope,
  ScheduleDraft,
  ScheduleRecipient,
} from '../../../../types/lexistar-reports';

const CADENCES: Array<{ value: CadenceKind; label: string }> = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'shift_end', label: 'Shift end' },
  { value: 'custom', label: 'Custom' },
];

const PERIOD_KEYS = new Set(['from', 'to', 'date', 'period']);
/** The shared input look without its full width (for inputs that sit inline). */
const inlineInput = inputClass.replace('w-full ', '');

function emptyDraft(def: ReportDefinition | undefined): ScheduleDraft {
  const fields = schemaFields(def?.parameters);
  const periodic = fields.some((f) => f.kind === 'daterange' || f.name === 'date');
  return {
    report_key: def?.key || '',
    name: def ? `${def.name} — daily` : '',
    parameters: { ...(periodic ? { period: 'yesterday' } : {}), ...defaultParams(def?.parameters) },
    cron: DEFAULT_CADENCE.cron,
    tz: DEFAULT_TIME_ZONE,
    formats: def?.formats.includes('pdf') ? ['pdf'] : (def?.formats.filter((f) => f !== 'html').slice(0, 1) as ReportFormat[]) || [],
    recipients: [],
    channels: ['email'],
    active: true,
  };
}

// ── Recipients ──────────────────────────────────────────────────────────────

function ScopeEditor({
  scope,
  onChange,
  idPrefix,
}: {
  scope: ReportScope;
  onChange: (next: ReportScope | null) => void;
  idPrefix: string;
}) {
  const entries = SCOPE_KEYS.filter((k) => scope[k.value] !== undefined);
  const unused = SCOPE_KEYS.filter((k) => scope[k.value] === undefined);
  const setKey = (key: keyof ReportScope, text: string) =>
    onChange({ ...scope, [key]: scopeValues(key, text) } as ReportScope);
  const remove = (key: keyof ReportScope) => {
    const next = { ...scope };
    delete next[key];
    onChange(Object.keys(next).length ? next : null);
  };
  return (
    <div className="mt-2 pl-3 border-l-2 border-slate-200 space-y-2" role="group" aria-label="Recipient scope">
      <p className="text-[11px] text-slate-500">Only for this recipient: the report is narrowed to…</p>
      {entries.map((k) => (
        <div key={k.value} className="flex items-center gap-2">
          <span className="w-28 flex-shrink-0 text-xs text-slate-600">{k.label}</span>
          <input
            id={`${idPrefix}-${k.value}`}
            aria-label={`${k.label} for this recipient`}
            className={inputClass}
            defaultValue={(scope[k.value] as unknown[] | undefined)?.join(', ') || ''}
            placeholder={k.hint}
            onBlur={(e) => setKey(k.value, e.target.value)}
            onChange={(e) => setKey(k.value, e.target.value)}
          />
          <button type="button" onClick={() => remove(k.value)} className="p-1.5 text-slate-400 hover:text-red-600" aria-label={`Remove ${k.label} filter`}>
            <FaTimes size={11} />
          </button>
        </div>
      ))}
      {unused.length > 0 && (
        <div className="w-52">
          <ThemedSelect
            aria-label="Add a scope filter"
            value=""
            onChange={(e) => {
              const key = String(e.target.value) as keyof ReportScope;
              if (key) onChange({ ...scope, [key]: [] } as ReportScope);
            }}
            className={inputClass}
          >
            <option value="">Add a filter…</option>
            {unused.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </ThemedSelect>
        </div>
      )}
    </div>
  );
}

function RecipientRow({
  r,
  i,
  users,
  groups,
  onChange,
  onRemove,
}: {
  r: ScheduleRecipient;
  i: number;
  users: UserOption[];
  groups: UserOption[];
  onChange: (patch: Partial<ScheduleRecipient>) => void;
  onRemove: () => void;
}) {
  const meta = RECIPIENT_KINDS.find((k) => k.value === r.kind);
  const refInput = () => {
    if (r.kind === 'user' && users.length) {
      return (
        <ThemedSelect
          aria-label={`Recipient ${i + 1} user`}
          value={r.ref}
          onChange={(e) => {
            const u = users.find((x) => x.id === String(e.target.value));
            onChange({ ref: String(e.target.value), name: u?.label });
          }}
          className={inputClass}
        >
          <option value="">Choose a user…</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.label}
            </option>
          ))}
        </ThemedSelect>
      );
    }
    if (r.kind === 'group' && groups.length) {
      return (
        <ThemedSelect
          aria-label={`Recipient ${i + 1} group`}
          value={r.ref}
          onChange={(e) => {
            const g = groups.find((x) => x.id === String(e.target.value));
            onChange({ ref: String(e.target.value), name: g?.label });
          }}
          className={inputClass}
        >
          <option value="">Choose a group…</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.label}
            </option>
          ))}
        </ThemedSelect>
      );
    }
    return (
      <input
        aria-label={`Recipient ${i + 1} ${meta?.label.toLowerCase() || 'value'}`}
        className={inputClass}
        value={r.ref}
        placeholder={r.kind === 'user' ? 'User id' : r.kind === 'group' ? 'Group id' : meta?.hint}
        onChange={(e) => onChange({ ref: e.target.value })}
      />
    );
  };
  return (
    <li className="py-2 border-b border-slate-100 last:border-0">
      <div className="flex flex-col sm:flex-row gap-2 sm:items-start">
        <div className="sm:w-52 flex-shrink-0">
          <ThemedSelect
            aria-label={`Recipient ${i + 1} kind`}
            value={r.kind}
            onChange={(e) => onChange({ kind: String(e.target.value) as RecipientKind, ref: '', name: undefined })}
            className={inputClass}
          >
            {RECIPIENT_KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </ThemedSelect>
        </div>
        <div className="flex-1 min-w-0">{refInput()}</div>
        <div className="flex items-center gap-1">
          {!r.scope && (
            <button
              type="button"
              onClick={() => onChange({ scope: {} })}
              className="px-2 py-2 text-xs font-medium text-primary-700 hover:underline whitespace-nowrap"
            >
              Limit scope
            </button>
          )}
          <button type="button" onClick={onRemove} aria-label={`Remove recipient ${i + 1}`} className="p-2 text-slate-400 hover:text-red-600">
            <FaTimes size={12} />
          </button>
        </div>
      </div>
      {r.scope && <ScopeEditor scope={r.scope} idPrefix={`rcp-${i}`} onChange={(scope) => onChange({ scope })} />}
    </li>
  );
}

// ── Cadence ─────────────────────────────────────────────────────────────────

function CadenceEditor({ value, onChange }: { value: Cadence; onChange: (c: Cadence) => void }) {
  const set = (patch: Partial<Cadence>) => onChange({ ...value, ...patch });
  // Phone / touch (ST-00204): the send time on the shared time sheet instead of typing HH:MM.
  const sheetMode = usePickerSheetMode();
  return (
    <div className="space-y-2">
      <div role="radiogroup" aria-label="How often" className="flex flex-wrap gap-1">
        {CADENCES.map((c) => (
          <button
            key={c.value}
            type="button"
            role="radio"
            aria-checked={value.kind === c.value}
            onClick={() => set({ kind: c.value, cron: value.kind === 'custom' ? value.cron : cadenceToCron(value) })}
            className={`px-3 py-1.5 rounded-lg text-sm border ${value.kind === c.value ? 'bg-primary-600 text-white border-primary-600' : 'bg-surface text-slate-700 border-slate-300 hover:bg-slate-50'}`}
          >
            {c.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
        {value.kind === 'weekly' && (
          <>
            on
            <div className="w-40">
              <ThemedSelect aria-label="Weekday" value={String(value.weekday)} onChange={(e) => set({ weekday: Number(e.target.value) })} className={inputClass}>
                {WEEKDAYS.map((d, i) => (
                  <option key={d} value={String(i)}>
                    {d}
                  </option>
                ))}
              </ThemedSelect>
            </div>
          </>
        )}
        {value.kind === 'monthly' && (
          <>
            on day
            <input
              aria-label="Day of the month"
              type="number"
              min={1}
              max={28}
              value={value.monthday}
              onChange={(e) => set({ monthday: Number(e.target.value) })}
              className={`${inlineInput} w-20`}
            />
          </>
        )}
        {['daily', 'weekly', 'monthly'].includes(value.kind) && (
          <>
            at
            {sheetMode ? (
              <ThemedTimePicker
                value={value.time || '06:00'}
                onChange={(e) => set({ time: e.target.value })}
                label="Send at"
              />
            ) : (
              <input
                aria-label="Time (HH:MM)"
                value={value.time}
                onChange={(e) => set({ time: e.target.value })}
                placeholder="06:00"
                className={`${inlineInput} w-24 font-mono`}
              />
            )}
          </>
        )}
        {value.kind === 'shift_end' && (
          <>
            at the end of each shift:
            <input
              aria-label="Shift ends (HH:MM, comma-separated)"
              value={value.shiftEnds.join(', ')}
              onChange={(e) =>
                set({
                  shiftEnds: e.target.value
                    .split(',')
                    .map((x) => x.trim())
                    .filter(Boolean),
                })
              }
              placeholder="06:00, 18:00"
              className={`${inlineInput} w-44 font-mono`}
            />
          </>
        )}
        {value.kind === 'custom' && (
          <input
            aria-label="Cron expression"
            value={value.cron}
            onChange={(e) => set({ cron: e.target.value })}
            placeholder="minute hour day month weekday"
            className={`${inlineInput} w-56 font-mono`}
          />
        )}
      </div>
    </div>
  );
}

// ── The editor ──────────────────────────────────────────────────────────────

export default function ScheduleEditor({
  open,
  schedule,
  definitions,
  initialReportKey,
  users,
  groups,
  onClose,
  onSaved,
}: {
  open: boolean;
  schedule: ReportSchedule | null;
  definitions: ReportDefinition[];
  initialReportKey?: string | null;
  users: UserOption[];
  groups: UserOption[];
  onClose: () => void;
  onSaved: (s: ReportSchedule) => void;
}) {
  const [draft, setDraft] = useState<ScheduleDraft>(() => emptyDraft(undefined));
  const [cadence, setCadence] = useState<Cadence>(DEFAULT_CADENCE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setShowErrors(false);
    if (schedule) {
      const { id: _id, last_run_at: _l, next_run_at: _n, ...rest } = schedule;
      setDraft({
        ...rest,
        // ST-00273: a zone typed in another case shows as the list's entry.
        tz: rest.tz ? canonicalTimeZone(rest.tz) : DEFAULT_TIME_ZONE,
        recipients: rest.recipients.map((r) => ({ ...r, ref: String(r.ref ?? '') })),
      });
      setCadence(cronToCadence(schedule.cron));
    } else {
      const def = definitions.find((d) => d.key === initialReportKey) || definitions.find((d) => d.key === 'daily_management_summary');
      setDraft(emptyDraft(def));
      setCadence(DEFAULT_CADENCE);
    }
  }, [open, schedule, definitions, initialReportKey]);

  const def = definitions.find((d) => d.key === draft.report_key);
  const fields = useMemo(() => schemaFields(def?.parameters), [def]);
  const formats = (def?.formats || []).filter((f) => f !== 'html');
  const errors = scheduleErrors(draft, cadence);
  const cron = cadenceToCron(cadence);

  if (!open) return null;

  const set = (patch: Partial<ScheduleDraft>) => setDraft((d) => ({ ...d, ...patch }));
  const toggle = <T,>(list: T[], v: T, on: boolean): T[] => (on ? [...new Set([...list, v])] : list.filter((x) => x !== v));

  const save = async () => {
    setShowErrors(true);
    if (errors.length) return;
    setBusy(true);
    setError(null);
    const body: ScheduleDraft = {
      ...draft,
      cron,
      name: draft.name.trim(),
      recipients: draft.recipients.map((r) => ({
        kind: r.kind,
        ref: r.ref.trim(),
        ...(r.scope && Object.keys(r.scope).length ? { scope: r.scope } : {}),
      })),
    };
    try {
      const saved = schedule
        ? await updateSchedule(schedule.id, { ...body, ...(schedule.version ? { version: schedule.version } : {}) })
        : await createSchedule(body);
      onSaved(saved);
    } catch (err) {
      setError(errorText(err, 'The schedule could not be saved.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={schedule ? `Edit schedule ${schedule.name}` : 'New schedule'}
        className="h-full w-full max-w-3xl bg-surface shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900">{schedule ? 'Edit schedule' : 'New schedule'}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-600">
            <FaTimes size={16} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {error && (
            <div role="alert" className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}
          <section className="grid grid-cols-1 sm:grid-cols-2 gap-4" aria-label="What">
            <div>
              <label htmlFor="sched-name" className="block text-xs font-semibold text-slate-600 mb-1">
                Name
              </label>
              <input id="sched-name" className={inputClass} value={draft.name} onChange={(e) => set({ name: e.target.value })} />
            </div>
            <div>
              <span className="block text-xs font-semibold text-slate-600 mb-1">Report</span>
              <ThemedSelect
                aria-label="Report"
                value={draft.report_key}
                onChange={(e) => {
                  const next = definitions.find((d) => d.key === String(e.target.value));
                  const fresh = emptyDraft(next);
                  set({ report_key: fresh.report_key, parameters: fresh.parameters, formats: fresh.formats });
                }}
                className={inputClass}
                disabled={!!schedule}
              >
                <option value="">Choose a report…</option>
                {definitions.map((d) => (
                  <option key={d.key} value={d.key}>
                    {d.name}
                  </option>
                ))}
              </ThemedSelect>
            </div>
          </section>

          {def && (
            <section aria-label="Parameters">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Parameters</h3>
              <ParameterForm
                fields={fields}
                value={draft.parameters}
                onChange={(parameters) => set({ parameters })}
                idPrefix="sched-param"
                allowRelative
                hide={['scope']}
              />
              {fields.some((f) => PERIOD_KEYS.has(f.name) || f.kind === 'daterange') && (
                <p className="mt-2 text-[11px] text-slate-500">
                  Choose a relative period so every run reports on its own window (yesterday, last week…).
                </p>
              )}
            </section>
          )}

          <section aria-label="When">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">When</h3>
            <CadenceEditor value={cadence} onChange={setCadence} />
            <p className="mt-2 text-xs text-slate-500">
              {describeCron(cron)} · <span className="font-mono">{cron}</span>
            </p>
            {/* ST-00273: picked from the IANA list, not typed. */}
            <div className="mt-3 flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3">
              <label htmlFor="sched-tz" className="text-xs font-semibold text-slate-600 sm:pt-2.5 whitespace-nowrap">
                Time zone
              </label>
              <div className="w-full sm:w-96">
                <TimeZoneSelect id="sched-tz" value={draft.tz} onChange={(tz) => set({ tz })} className={inputClass} />
              </div>
            </div>
          </section>

          <section className="grid grid-cols-1 sm:grid-cols-2 gap-4" aria-label="How">
            <fieldset>
              <legend className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Formats</legend>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {formats.map((f) => (
                  <label key={f} className="inline-flex items-center gap-1.5 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={draft.formats.includes(f)}
                      onChange={(e) => set({ formats: toggle(draft.formats, f, e.target.checked) })}
                    />
                    {FORMAT_LABELS[f]}
                  </label>
                ))}
                {!formats.length && <span className="text-sm text-slate-500">Choose a report first.</span>}
              </div>
            </fieldset>
            <fieldset>
              <legend className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Channels</legend>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {CHANNELS.map((c) => (
                  <label key={c.value} className="inline-flex items-center gap-1.5 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={draft.channels.includes(c.value)}
                      onChange={(e) => set({ channels: toggle<Channel>(draft.channels, c.value, e.target.checked) })}
                    />
                    {c.label}
                  </label>
                ))}
              </div>
            </fieldset>
          </section>

          <section aria-label="Recipients">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Recipients</h3>
            <ul>
              {draft.recipients.map((r, i) => (
                <RecipientRow
                  key={i}
                  r={r}
                  i={i}
                  users={users}
                  groups={groups}
                  onChange={(patch) =>
                    set({ recipients: draft.recipients.map((x, j) => (j === i ? { ...x, ...patch } : x)) })
                  }
                  onRemove={() => set({ recipients: draft.recipients.filter((_, j) => j !== i) })}
                />
              ))}
            </ul>
            <button
              type="button"
              onClick={() => set({ recipients: [...draft.recipients, { kind: 'user', ref: '' }] })}
              className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary-700 hover:underline"
            >
              <FaPlus size={9} /> Add recipient
            </button>
          </section>

          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={draft.active} onChange={(e) => set({ active: e.target.checked })} />
            Active — runs on schedule
          </label>

          {showErrors && errors.length > 0 && (
            <ul role="alert" aria-label="Schedule problems" className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900 list-disc pl-6">
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          )}
        </div>
        <footer className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="px-5 py-2 text-sm font-semibold text-white rounded-lg shadow-sm bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
          >
            {busy ? 'Saving…' : schedule ? 'Save schedule' : 'Create schedule'}
          </button>
        </footer>
      </aside>
    </div>
  );
}
